import React, { FC, useRef, useCallback, useState } from 'react'
import cn from '../../../../../../lib/utils/cn'
import { useCardComments } from '../../../../../../hooks/api/boardHooks'
import Loader from '../../../../../common/Loader'
import { CommentsFormValues, UserAvatar } from '..'
import { formatCommentDate } from '../../../../../../lib/utils/dateFormatter'
import { Button, Tooltip } from '../../../../../ui'
import { CurrentUserObject } from '../../../../../../lib/api/types/user.interfaces'
import { useFormContext } from 'react-hook-form'
import { useAuth } from '../../../../../../hooks'

interface CommentsListProps {
	cardId?: string
	boardId?: string
	isShouldFetch?: boolean
	currentUser?: CurrentUserObject
	onDelete: (id: string) => void
}

const CommentsList: FC<CommentsListProps> = ({
	cardId,
	boardId,
	isShouldFetch,
	currentUser,
	onDelete,
}) => {
	const { isAuthenticated } = useAuth()
	const { reset, getValues } = useFormContext<CommentsFormValues>()
	const commentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
	const [highlightedComment, setHighlightedComment] = useState<string | null>(
		null
	)

	const { data: cardComments, isLoading: isLoadingComments } = useCardComments(
		{
			boardId: boardId ?? '',
			cardId: cardId ?? '',
		},
		{ enabled: Boolean(boardId && cardId && isAuthenticated && isShouldFetch) }
	)

	const scrollToComment = useCallback(
		(commentId: string) => {
			const commentElement = commentRefs.current[commentId]
			if (commentElement) {
				const commentIndex =
					cardComments?.data?.findIndex((c) => c.id === commentId) ?? -1

				commentElement.scrollIntoView({
					behavior: 'auto',
					block: commentIndex === 0 ? 'start' : 'center',
				})

				setHighlightedComment(commentId)
				setTimeout(() => {
					setHighlightedComment(null)
				}, 2000)
			}
		},
		[cardComments?.data]
	)

	return (
		<div
			className={cn(
				'flex flex-col gap-4 max-h-[100px] md:h-full max-w-[388px] box-border',
				(isLoadingComments || !cardComments?.data?.length) &&
				'flex items-center justify-center'
			)}
		>
			{isLoadingComments || !cardComments?.data?.length ? (
				<div className="w-full h-full flex items-center justify-center">
					{isLoadingComments ? (
						<Loader classNameWrapper="mx-auto" />
					) : (
						<p className="text-stroke-dark italic text-sm text-center dark:text-text-table-dark">
							No comments yet
						</p>
					)}
				</div>
			) : (
				cardComments?.data?.map((comment) => {
					const isCommentOwner = currentUser?.id === comment?.createdBy?.id
					const repliedTo = cardComments?.data?.find(
						(rc) => rc?.id === comment?.parentId
					)
					const isHighlighted = highlightedComment === comment.id

					return (
						<div
							key={comment.id}
							className={cn(
								'flex gap-3 rounded-lg bg-white dark:bg-background-dark duration-default dark:border-stroke-light-dark border-stroke-light items-start',
								isHighlighted && 'animate-pulse-quick'
							)}
							ref={(el) => {
								commentRefs.current[comment.id] = el
							}}
						>
							<UserAvatar
								user={comment.createdBy}
								className="w-8 h-8  min-w-8"
							/>
							<div className="flex flex-col gap-2">
								<div className="flex items-center gap-2">
									<span className="text-sm font-medium text-text-main dark:text-text-main-dark">
										{comment.createdBy?.name ?? 'Unknown user'}
									</span>
									<span className="text-xs text-text-secondary dark:text-text-table-dark">
										{formatCommentDate(comment.createdAt)}
										{
											!!comment?.modifiedAt && ' (edited)'
											// ` (edited ${formatCommentDate(comment?.modifiedAt)})`
										}
									</span>
								</div>
								{!!repliedTo?.id && (
									<Tooltip label="Click to scroll">
										<div
											className="flex-1 w-full border-l-2  border-stroke-dark pl-2 group/replied max-w-[300px] cursor-pointer hover:border-accent duration-default"
											onClick={() => scrollToComment(repliedTo.id)}
										>
											<p className="truncate text-xs text-stroke-dark group-hover/replied:text-text-secondary duration-default dark:text-text-table-dark">
												Replied to:{' '}
												{repliedTo?.createdBy?.name ||
													repliedTo?.createdBy?.email}
											</p>
											<p className="truncate text-sm text-text-secondary duration-default group-hover/replied:text-text-main dark:group-hover/replied:text-text-main-dark duration-default dark:text-text-table-dark">
												{repliedTo?.content}
											</p>
										</div>
									</Tooltip>
								)}
								<p className="text-sm text-text-main dark:text-text-main-dark">
									{comment.content}
								</p>
								<div className="flex items-center justify-start gap-2">
									<Button
										label="Reply"
										variant="link"
										onClick={() =>
											reset({
												...getValues(),
												content: '',
												repliedComment: comment,
												editingComment: null,
											})
										}
										className="text-text-secondary dark:text-text-table-dark font-normal text-sm no-underline  hover:text-accent dark:hover:text-accent"
									/>
									{isCommentOwner && (
										<Button
											label="Edit"
											variant="link"
											onClick={() =>
												reset({
													...getValues(),
													content: comment?.content,
													repliedComment: null,
													editingComment: comment,
												})
											}
											className="text-text-secondary dark:text-text-table-dark font-normal text-sm no-underline hover:text-accent dark:hover:text-accent"
										/>
									)}
									{isCommentOwner && (
										<Button
											label="Delete"
											variant="link"
											onClick={() => onDelete(comment?.id)}
											className="text-text-secondary dark:text-text-table-dark font-normal text-sm no-underline  hover:text-accent dark:hover:text-accent"
										/>
									)}
								</div>
							</div>
						</div>
					)
				})
			)}
		</div>
	)
}

export default CommentsList
